/* eslint-disable react/no-array-index-key */
/* eslint-disable react/jsx-no-literals */
// components/budget-panel/SpendView.tsx
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { BudgetCircularProgress } from '@/features/canvas/components/InitiativeDialog/BudgetCircularProgress';
import { useEditorContext } from '@/features/canvas/contexts/editor-context';
import {
  BudgetSummary,
  FilterBudget,
  SectionBudget,
  TimePeriodBudget,
} from '@/features/canvas/types/budget-type';
import { formatCurrency } from '@/features/canvas/utils/currency';

import { BreakdownSelector } from './BreakdownSelector';

interface Props {
  breakdownView: string;
  currency: string;
  filterBreakdowns: Record<number, FilterBudget[]>;
  isLoading: boolean;
  onBreakdownChange: (value: string) => void;
  sectionBreakdown: SectionBudget[];
  summary: BudgetSummary | undefined;
  timePeriodBreakdown: TimePeriodBudget[];
}

export const SpendView: React.FC<Props> = ({
  summary,
  timePeriodBreakdown,
  sectionBreakdown,
  filterBreakdowns,
  currency,
  isLoading,
  breakdownView,
  onBreakdownChange,
}) => {
  const { t } = useTranslation();
  const { filters } = useEditorContext();

  const totalBudget = summary?.total_budget || 0;
  const totalSpend = summary?.total_spend || 0;
  const remaining = totalBudget - totalSpend;
  const remainingPercentage = totalBudget > 0 ? (remaining / totalBudget) * 100 : 0;
  const isOverBudget = remaining < 0;

  const getCurrencySymbol = (currencyCode: string) => {
    const symbols: Record<string, string> = {
      GBP: '£',
      USD: '$',
      EUR: '€',
      AUD: 'A$',
      CAD: 'C$',
      AED: 'د.إ',
      JPY: '¥',
      CNY: '¥',
      DKK: 'kr',
      HKD: 'HK$',
      INR: '₹',
      NOK: 'kr',
      QAR: 'ر.ق',
      SAR: 'ر.س',
      SGD: 'S$',
      SEK: 'kr',
      ZAR: 'R',
      PKR: '₨',
      NZD: 'NZ$',
    };
    return symbols[currencyCode] || currencyCode;
  };

  const currencySymbol = getCurrencySymbol(currency);

  if (isLoading) {
    return <LoadingState>{t('loading')}</LoadingState>;
  }

  // Helper function to render filter breakdowns
  const renderFilterBreakdown = (filterId: number) => {
    const breakdowns = filterBreakdowns[filterId] || [];
    const filter = filters?.find(f => f.id === filterId);

    return breakdowns.map(filterOption => {
      const optionRemaining = filterOption.total_budget - filterOption.spend;
      const optionRemainingPercentage =
        filterOption.total_budget > 0
          ? Math.round((optionRemaining / filterOption.total_budget) * 100)
          : 0;
      const optionSpendPercentage =
        filterOption.total_budget > 0
          ? Math.round((filterOption.spend / filterOption.total_budget) * 100)
          : 0;

      // Find the color for this option
      const optionColor =
        filter?.options?.find(opt => opt.id === filterOption.filter_option_id)?.color || '#6e6e72';

      return (
        <BreakdownItem key={filterOption.filter_option_id}>
          <ItemHeader>
            <ItemTitleWrapper>
              <ColorCircle $color={optionColor} />
              <ItemTitle>{filterOption.filter_option_title}</ItemTitle>
            </ItemTitleWrapper>
          </ItemHeader>
          <Divider />
          <SpendBreakdown>
            <GridRow>
              <SpendLabel>{t('editor.sidebar.initiative.budget.remaining')}</SpendLabel>
              <SpendValue $color="#616161">
                {formatCurrency(Math.abs(optionRemaining), currency)}
              </SpendValue>
              <SpendPercentage>{Math.max(0, optionRemainingPercentage)}%</SpendPercentage>
            </GridRow>
            <GridRow>
              <SpendLabel>{t('editor.sidebar.initiative.budget.spent')}</SpendLabel>
              <SpendValue $color="#a5a5a5">
                {formatCurrency(filterOption.spend, currency)}
              </SpendValue>
              <SpendPercentage>{optionSpendPercentage}%</SpendPercentage>
            </GridRow>
          </SpendBreakdown>
        </BreakdownItem>
      );
    });
  };

  return (
    <Container>
      <Section>
        <ChartWrapper>
          <ChartContainer>
            <BudgetCircularProgress
              percentage={remainingPercentage}
              spent={totalSpend}
              total={totalBudget}
              currencySymbol={currencySymbol}
              isOverBudget={isOverBudget}
            />
          </ChartContainer>
        </ChartWrapper>
      </Section>

      <Section>
        <BreakdownHeader>
          <DisplayByLabel>Display by:</DisplayByLabel>
          <BreakdownSelector value={breakdownView} onChange={onBreakdownChange} />
        </BreakdownHeader>

        <BreakdownList>
          {breakdownView === 'timePeriod' &&
            timePeriodBreakdown?.map(period => {
              const periodRemaining = period.total_budget - period.spend;
              const periodRemainingPercentage =
                period.total_budget > 0
                  ? Math.round((periodRemaining / period.total_budget) * 100)
                  : 0;
              const periodSpendPercentage =
                period.total_budget > 0
                  ? Math.round((period.spend / period.total_budget) * 100)
                  : 0;

              return (
                <BreakdownItem key={period.time_period_id}>
                  <ItemHeader>
                    <ItemTitle>{period.time_period}</ItemTitle>
                  </ItemHeader>
                  <Divider />
                  <SpendBreakdown>
                    <GridRow>
                      <SpendLabel>{t('editor.sidebar.initiative.budget.remaining')}</SpendLabel>
                      <SpendValue $color="#616161">
                        {formatCurrency(periodRemaining, currency)}
                      </SpendValue>
                      <SpendPercentage>{Math.max(0, periodRemainingPercentage)}%</SpendPercentage>
                    </GridRow>
                    <GridRow>
                      <SpendLabel>{t('editor.sidebar.initiative.budget.spent')}</SpendLabel>
                      <SpendValue $color="#a5a5a5">
                        {formatCurrency(period.spend, currency)}
                      </SpendValue>
                      <SpendPercentage>{periodSpendPercentage}%</SpendPercentage>
                    </GridRow>
                  </SpendBreakdown>
                </BreakdownItem>
              );
            })}

          {breakdownView === 'section' &&
            sectionBreakdown?.map(section => {
              const sectionRemaining = section.total_budget - section.spend;
              const sectionRemainingPercentage =
                section.total_budget > 0
                  ? Math.round((sectionRemaining / section.total_budget) * 100)
                  : 0;
              const sectionSpendPercentage =
                section.total_budget > 0
                  ? Math.round((section.spend / section.total_budget) * 100)
                  : 0;

              return (
                <BreakdownItem key={section.section_id}>
                  <ItemHeader>
                    <ItemTitle>{section.section}</ItemTitle>
                  </ItemHeader>
                  <Divider />
                  <SpendBreakdown>
                    <GridRow>
                      <SpendLabel>{t('editor.sidebar.initiative.budget.remaining')}</SpendLabel>
                      <SpendValue $color="#616161">
                        {formatCurrency(Math.abs(sectionRemaining), currency)}
                      </SpendValue>
                      <SpendPercentage>{Math.max(0, sectionRemainingPercentage)}%</SpendPercentage>
                    </GridRow>
                    <GridRow>
                      <SpendLabel>{t('editor.sidebar.initiative.budget.spent')}</SpendLabel>
                      <SpendValue $color="#a5a5a5">
                        {formatCurrency(section.spend, currency)}
                      </SpendValue>
                      <SpendPercentage>{sectionSpendPercentage}%</SpendPercentage>
                    </GridRow>
                  </SpendBreakdown>
                </BreakdownItem>
              );
            })}

          {breakdownView.startsWith('filter-') &&
            renderFilterBreakdown(parseInt(breakdownView.replace('filter-', '')))}
        </BreakdownList>
      </Section>
    </Container>
  );
};

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2rem;
`;

const Section = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

const BreakdownHeader = styled.div`
  padding: 0 1.5rem;
  display: flex;
  justify-content: space-between;
  margin-bottom: 0.5rem;
`;

const DisplayByLabel = styled.span`
  font-size: 1rem;
  font-weight: 600;
  color: #363649;
  margin-right: 0.75rem;
  display: flex;
  align-items: center;
`;

const ChartWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  background: ${({ theme }) => theme.palette.background.paper};
  border-radius: 0.5rem;
`;

const ChartContainer = styled.div`
  width: 125px;
  height: 125px;
`;

const BreakdownList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const BreakdownItem = styled.div`
  padding: 1rem;
  background: ${({ theme }) => theme.palette.background.paper};
  border-radius: 0.5rem;
`;

const ItemHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.75rem;
`;

const ItemTitleWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
`;

const ColorCircle = styled.div<{ $color: string }>`
  width: 1.25rem;
  height: 1.25rem;
  border-radius: 50%;
  background-color: ${({ $color }) => $color};
  flex-shrink: 0;
`;

const ItemTitle = styled.span`
  font-size: 1.5rem;
  font-weight: 600;
  color: #9a9aa3;
`;

const Divider = styled.div`
  height: 1px;
  background-color: #26a5a2;
  opacity: 32%;
  margin-bottom: 1.25rem;
`;

const SpendBreakdown = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const GridRow = styled.div`
  display: grid;
  grid-template-columns: 60px 1fr 50px;
  gap: 0.5rem;
  align-items: center;
`;

const SpendLabel = styled.span`
  font-size: 1rem;
  font-weight: 600;
  color: ${({ theme }) => theme.palette.brand.editorTabSecondary};
`;

const SpendValue = styled.span<{ $color?: string }>`
  font-size: 1.5rem;
  font-weight: 600;
  color: ${({ $color, theme }) => $color || theme.palette.text.primary};
  text-align: right;
`;

const SpendPercentage = styled.span`
  font-size: 1.5rem;
  font-weight: 600;
  color: #363649;
  text-align: right;
`;

const LoadingState = styled.div`
  text-align: center;
  padding: 2rem;
  color: ${({ theme }) => theme.palette.text.secondary};
`;
